/* eslint-disable max-len */
import {createContext, useState, useEffect} from 'react';

import useCustomer from '../utils/store-context-hooks/customer-hook';
import useCartFlyout from '../utils/store-context-hooks/cartFlyout-hook';
import useCheckout from '../utils/store-context-hooks/checkout-hook';
import useDiscount from '../utils/store-context-hooks/discount-hook';
import useAffiliate from '../utils/store-context-hooks/affiliate-hook';
import useStudios from '../utils/store-context-hooks/studios-hook';
import useEducation from '../utils/store-context-hooks/education-hook';
import useDistributor from '../utils/store-context-hooks/distributor-hook';
import useModal from '../utils/store-context-hooks/modal-hook';

const defaultValues = {
	loading: false,
	setLoading: () => {},
	// Customer
	getUserIDToken: async() => {},
	customer: {
		loading: true,
		isLoggedIn: false,
		auth0UserState: null,
	},
	setCustomer: () => {},
	loginUser: () => {},
	logoutUser: () => {},
	signUpUser: () => {},
	handleRoleButtonClick: () => {},
	handleEmailButtonClick: () => {},
	createAddress: () => {},
	updateAddress: () => {},
	removeAddress: () => {},
	isLoginDisabled: false,
	// Cart
	isShoppingCartOffline: false,
	isCartFlyoutOpen: false,
	cartFlyoutOpen: () => {},
	cartFlyoutClose: () => {},
	cartError: {
		visible: false,
		errorHeading: '',
		errorBody: '',
		errorEmail: '',
	},
	setCartError: () => {},
	// Checkout
	envoyClient: null,
	checkout: {
		lineItems: [],
		totalQuantity: 0,
	},
	classCheckout: {
		lineItems: [],
		totalQuantity: 0,
	},
	setCheckoutLoadingState: () => {},
	checkEventClassesAvailability: () => {},
	setCheckoutItem: () => {},
	setCheckoutAttributes: () => {},
	addVariantsToCart: () => {},
	lastAdded: null,
	removeLineItems: () => {},
	updateLineItem: () => {},
	// Affiliate
	affiliateFunc: () => () => {},
	// Studios
	studiosData: {
		studios: [],
		allFilters: {},
	},
	setStudiosData: () => {},
	studioFinderForm: false,
	setStudioFinderForm: () => {},
	contentfulApi: null,
	// Education
	locationOptions: [],
	// Distributor
	distributor: false,
	setDistributor: () => {},
	locationInfo: {},
	// Modals
	alertModal: false,
	setAlertModal: () => {},
};

export const StoreContext = createContext(defaultValues);

export const StoreProvider = ({ children }) => {
	// ----------
	//  State
	// ----------
	const [loading, setLoading] = useState(defaultValues.loading);
	const [affiliateFunc, setAffiliateFunc] = useState((defaultValues.affiliateFunc));

	// ----------
	//  Hooks
	// ----------

	// Customer Hook
	const {
		getUserIDToken,
		customer,
		setCustomer,
		loginUser,
		logoutUser,
		signUpUser,
		handleRoleButtonClick,
		handleEmailButtonClick,
		createAddress,
		updateAddress,
		removeAddress,
		isLoginDisabled,
	} = useCustomer();
	// Cart Hook
	const {
		isShoppingCartOffline,
		isCartFlyoutOpen,
		cartFlyoutOpen,
		cartFlyoutClose,
		cartError,
		setCartError,
	} = useCartFlyout();
	// Checkout Hook
	const {
		checkoutLoading,
		envoyClient,
		checkout,
		classCheckout,
		lastAdded,
		setCheckoutLoadingState,
		checkEventClassesAvailability,
		setCheckoutItem,
		setCheckoutAttributes,
		addVariantsToCart,
		updateLineItem,
		updateLineItems,
		removeLineItems,
	} = useCheckout({
		affiliateFunc, cartFlyoutOpen, setCartError, isCartFlyoutOpen,
	});
	// Discount Hook
	const {
		updateCheckoutDiscount,
	} = useDiscount({
		envoyClient, checkout,
	});
	// Affiliate Hook
	const {
		affiliateLoading,
	} = useAffiliate({
		setAffiliateFunc,
		checkout,
		setCheckoutItem,
		updateLineItems,
		updateCheckoutDiscount,
		isCartFlyoutOpen,
	});
	// Studios Hook
	const {
		studiosData,
		setStudiosData,
		studioFinderForm,
		setStudioFinderForm,
		contentfulApi,
	} = useStudios();
	// Education Hook
	const {
		locationOptions,
	} = useEducation();
	// Distributor Hook
	const {
		distributor,
		setDistributor,
		locationInfo,
	} = useDistributor();
	// Modal Hook
	const {
		alertModal,
		setAlertModal,
	} = useModal();

	// Loading State
	useEffect(() => {
		const isLoading = affiliateLoading || checkoutLoading;
		const timer = setTimeout(() => {
			setLoading(() => isLoading);
		}, isLoading ? 0 : 100); // add delay when setting loading to false to avoid quick flickering

		return () => clearTimeout(timer);
	}, [affiliateLoading, checkoutLoading]);

	return (
		<StoreContext.Provider
			value={{
				loading,
				// Customer
				getUserIDToken,
				customer,
				setCustomer,
				loginUser,
				logoutUser,
				signUpUser,
				handleRoleButtonClick,
				handleEmailButtonClick,
				createAddress,
				updateAddress,
				removeAddress,
				isLoginDisabled,
				// Cart
				isShoppingCartOffline,
				isCartFlyoutOpen,
				cartFlyoutOpen,
				cartFlyoutClose,
				cartError,
				setCartError,
				// Checkout
				envoyClient,
				checkout,
				classCheckout,
				lastAdded,
				setCheckoutLoadingState,
				checkEventClassesAvailability,
				setCheckoutItem,
				setCheckoutAttributes,
				addVariantsToCart,
				removeLineItems,
				updateLineItem,
				// Affiliate
				affiliateFunc,
				// Studios
				studiosData,
				setStudiosData,
				studioFinderForm,
				setStudioFinderForm,
				contentfulApi,
				// Education
				locationOptions,
				// Distributor
				distributor,
				setDistributor,
				locationInfo,
				// Modal
				alertModal,
				setAlertModal,
			}}
		>
			{children}
		</StoreContext.Provider>
	);
};
